/**
 * A payment request asks the patient to pay. It is not an invoice_payments row
 * and it does not by itself settle the invoice or the appointment.
 */

export const PAYMENT_REQUEST_CHANNELS = [
  'invoice_email',
  'payment_link',
  'card',
  'mobilepay',
] as const;

export const PAYMENT_REQUEST_STATUSES = [
  'created',
  'sent',
  'pending',
  'expired',
  'cancelled',
  'completed',
  'failed',
] as const;

export type PaymentRequestChannel = (typeof PAYMENT_REQUEST_CHANNELS)[number];
export type PaymentRequestStatus = (typeof PAYMENT_REQUEST_STATUSES)[number];

const OPEN_STATUSES = new Set<PaymentRequestStatus>(['created', 'sent', 'pending']);

export function isPaymentRequestChannel(value: string): value is PaymentRequestChannel {
  switch (value) {
    case 'invoice_email':
    case 'payment_link':
    case 'card':
    case 'mobilepay':
      return true;
    default:
      return false;
  }
}

export function isPaymentRequestStatus(value: string): value is PaymentRequestStatus {
  switch (value) {
    case 'created':
    case 'sent':
    case 'pending':
    case 'expired':
    case 'cancelled':
    case 'completed':
    case 'failed':
      return true;
    default:
      return false;
  }
}

export function isOpenPaymentRequestStatus(status: string): boolean {
  return isPaymentRequestStatus(status) && OPEN_STATUSES.has(status);
}

export function paymentRequestChannelI18nKey(channel: PaymentRequestChannel): string {
  switch (channel) {
    case 'invoice_email':
      return 'invoices.payment_request_channel_invoice_email';
    case 'payment_link':
      return 'invoices.payment_request_channel_payment_link';
    case 'card':
      return 'invoices.payment_request_channel_card';
    case 'mobilepay':
      return 'invoices.payment_request_channel_mobilepay';
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

export function paymentRequestStatusI18nKey(status: PaymentRequestStatus): string {
  switch (status) {
    case 'created':
      return 'invoices.payment_request_status_created';
    case 'sent':
      return 'invoices.payment_request_status_sent';
    case 'pending':
      return 'invoices.payment_request_status_pending';
    case 'expired':
      return 'invoices.payment_request_status_expired';
    case 'cancelled':
      return 'invoices.payment_request_status_cancelled';
    case 'completed':
      return 'invoices.payment_request_status_completed';
    case 'failed':
      return 'invoices.payment_request_status_failed';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/** A new request is offered only while money is still outstanding. */
export function canOfferPaymentRequest(input: {
  outstandingCents: number;
  status: string;
}): boolean {
  if (input.outstandingCents <= 0) return false;
  return (
    input.status === 'issued' ||
    input.status === 'partial' ||
    input.status === 'overdue' ||
    input.status === 'expired_pay_link'
  );
}

/**
 * Provider capture matches a payment request by its own token or provider ids.
 * invoices.pay_token is not required to equal that token.
 * A cancelled request does not record money.
 */
export function paymentRequestAcceptsCapture(input: {
  requestStatus: string | null;
  requestToken: string | null;
  providerCheckoutId: string | null;
  providerReference: string | null;
  invoiceToken: string | null;
  payToken: string | null;
  paymentIntentId: string | null;
  checkoutSessionId: string | null;
}): boolean {
  if (input.requestStatus === 'cancelled') return false;
  const tokenMatchesRequest =
    input.payToken != null && input.payToken !== '' && input.payToken === input.requestToken;
  const checkoutMatches =
    input.checkoutSessionId != null &&
    input.checkoutSessionId !== '' &&
    (input.checkoutSessionId === input.providerCheckoutId ||
      input.checkoutSessionId === input.providerReference ||
      input.checkoutSessionId === input.requestToken);
  const intentMatches =
    input.paymentIntentId != null &&
    input.paymentIntentId !== '' &&
    (input.paymentIntentId === input.providerReference ||
      input.paymentIntentId === input.providerCheckoutId);
  if (input.requestToken || input.providerCheckoutId || input.providerReference) {
    return tokenMatchesRequest || checkoutMatches || intentMatches;
  }
  return (
    input.payToken != null &&
    input.payToken !== '' &&
    input.invoiceToken != null &&
    input.payToken === input.invoiceToken
  );
}
